/**
 * Admin Dashboard Sidebar - shadcn/ui implementation
 *
 * Uses shadcn Sidebar components for consistent UI patterns.
 * Navigation config is externalized to /src/config/navigation.ts.
 *
 * @see https://ui.shadcn.com/docs/components/sidebar
 */

import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useApprovalCounts } from '@/hooks/useApprovalCounts'
import { useTheme } from '@/hooks/use-theme'
import {
  navigationConfig,
  enhanceNavigationWithBadges,
  type NavItem
} from '@/config/navigation'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

/**
 * Main sidebar component for the admin dashboard.
 * Must be wrapped in SidebarProvider (typically in MainLayout).
 */
export function AppSidebar() {
  const location = useLocation()
  const { data: approvalCounts } = useApprovalCounts()
  const { state } = useSidebar()
  const { resolved: theme } = useTheme()
  const isCollapsed = state === 'collapsed'

  // Theme-aware logo selection
  const logoSrc = theme === 'dark'
    ? '/images/logos/fmb-logo-wide-white.svg'
    : '/images/logos/fmb-logo-wide-red.svg'

  // Enhance navigation with dynamic badge counts
  const navigationItems = enhanceNavigationWithBadges(navigationConfig, approvalCounts)

  /**
   * Check if a route is currently active
   */
  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  /**
   * Check if any child route of a parent is active
   */
  const isParentActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => isActive(child.href))
    }
    return isActive(item.href)
  }

  return (
    <Sidebar collapsible="icon">
      {/* Header with Logo */}
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-3 px-2 py-2">
          {isCollapsed ? (
            // Collapsed: show icon only
            <img
              src="/images/logos/fmb-icon-black.png"
              alt="FixMyBrick"
              className="w-8 h-8 object-contain dark:invert"
            />
          ) : (
            // Expanded: show full logo
            <img
              src={logoSrc}
              alt="FixMyBrick Admin Dashboard"
              className="h-8 w-auto object-contain"
            />
          )}
        </div>
      </SidebarHeader>

      {/* Navigation Content */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => (
                <NavMenuItem
                  key={item.href}
                  item={item}
                  isActive={isActive}
                  isParentActive={isParentActive}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="border-t border-sidebar-border">
        <div className="px-2 py-3 text-xs text-sidebar-foreground/60">
          {!isCollapsed && (
            <>
              <p>FixMyBrick Admin v1.0</p>
              <p>&copy; 2025 FixMyBrick Inc.</p>
            </>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}

interface NavMenuItemProps {
  item: NavItem
  isActive: (href: string) => boolean
  isParentActive: (item: NavItem) => boolean
}

/**
 * Individual navigation menu item.
 * Renders as a direct link or collapsible section based on children.
 */
function NavMenuItem({ item, isActive, isParentActive }: NavMenuItemProps) {
  const hasChildren = Boolean(item.children?.length)
  const parentActive = isParentActive(item)

  // Items without children - render as direct link
  if (!hasChildren) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={isActive(item.href)}
          tooltip={item.title}
        >
          <Link to={item.href}>
            <item.icon className="size-4" />
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
        {item.badge && (
          <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
            {item.badge}
          </SidebarMenuBadge>
        )}
      </SidebarMenuItem>
    )
  }

  // Items with children - render as collapsible section
  return (
    <Collapsible
      defaultOpen={parentActive}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            isActive={parentActive}
          >
            <item.icon className="size-4" />
            <span>{item.title}</span>
            <ChevronRight className="ml-auto size-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.children?.map((child) => (
              <SidebarMenuSubItem key={child.href}>
                <SidebarMenuSubButton
                  asChild
                  isActive={isActive(child.href)}
                >
                  <Link to={child.href}>
                    <child.icon className="size-4" />
                    <span>{child.title}</span>
                  </Link>
                </SidebarMenuSubButton>
                {child.badge && (
                  <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
                    {child.badge}
                  </SidebarMenuBadge>
                )}
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

// Default export for backwards compatibility with existing imports
export default AppSidebar
